import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plane, MapPin, Clock, Sparkles, Navigation, Camera, Crown, ArrowRight, Shield, Award, Users } from "lucide-react";
import { FLEET, SERVICES } from "../lib/api";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

const ICONS = { Plane, MapPin, Clock, Sparkles, Navigation, Camera, Crown };

export default function Home() {
  const navigate = useNavigate();
  const { data: reviews = [] } = useQuery({
    queryKey: ["reviews"],
    queryFn: async () => (await api.get("/reviews")).data,
  });

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <video
            autoPlay muted loop playsInline
            poster="https://images.unsplash.com/photo-1485291571150-772bcfc10da5?crop=entropy&cs=srgb&fm=jpg&q=80"
            className="w-full h-full object-cover"
          >
            <source src="https://videos.pexels.com/video-files/3066476/3066476-uhd_2560_1440_24fps.mp4" type="video/mp4"/>
          </video>
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,12,14,0.55) 0%, rgba(11,12,14,0.75) 55%, rgba(11,12,14,0.95) 100%)"}}/>
        </div>
        <div className="relative section-container pt-24 pb-32 lg:pt-40 lg:pb-48">
          <div className="fade-up max-w-3xl">
            <div className="eyebrow mb-6">Est. New York · Private Chauffeured Transportation</div>
            <h1 className="font-serif font-normal text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-white">
              Luxury Transportation.<br/>
              <span className="italic text-[color:var(--lume-gold-light)]">Seamless Experience.</span>
            </h1>
            <p className="mt-8 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
              Professional chauffeured transportation for airport transfers, point-to-point travel,
              special events, hourly service, and long-distance journeys.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button onClick={() => navigate("/booking")} className="btn-gold" data-testid="hero-book-ride-btn">Book a Ride</button>
              <button onClick={() => navigate("/contact?subject=quote")} className="btn-outline-gold" data-testid="hero-quote-btn">Request a Quote</button>
            </div>
            <div className="mt-14 grid grid-cols-3 gap-8 max-w-lg">
              {[
                { icon: Shield, k: "Vetted", v: "Master Chauffeurs" },
                { icon: Award, k: "Impeccable", v: "Luxury Fleet" },
                { icon: Users, k: "Executive", v: "Private Service" },
              ].map((s) => (
                <div key={s.k}>
                  <s.icon className="text-[color:var(--lume-gold)] mb-2" size={20}/>
                  <div className="font-serif text-white text-lg">{s.k}</div>
                  <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section-container py-24" data-testid="home-services">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
          <div>
            <div className="eyebrow mb-3">Our Services</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-white leading-tight max-w-xl">Every itinerary, considered.</h2>
          </div>
          <Link to="/services" className="text-sm text-[color:var(--lume-gold)] flex items-center gap-2 hover:gap-3 transition-all">All services <ArrowRight size={14}/></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            const isQuote = s.id === "photoshoot" || s.id === "limo";
            return (
              <button key={s.id} onClick={() => navigate(s.id === "limo" ? "/contact?subject=quote&vehicle=limo" : isQuote ? `/contact?subject=${s.id}` : `/booking?service=${s.id}`)} data-testid={`service-card-${s.id}`}
                className="lume-card p-6 text-left hover:-translate-y-1 transition-transform">
                <Icon className="text-[color:var(--lume-gold)] mb-4" size={22}/>
                <div className="font-serif text-white text-lg leading-tight">{s.title}</div>
                <div className="text-sm text-slate-400 mt-3 leading-relaxed">{s.subtitle}</div>
                <div className="mt-4 text-xs uppercase tracking-widest text-[color:var(--lume-gold)] opacity-80 flex items-center gap-2">{isQuote ? "Request a Quote" : "Explore"} <ArrowRight size={12}/></div>
              </button>
            );
          })}
        </div>
      </section>

      {/* FLEET PREVIEW */}
      <section className="py-24 border-t border-white/5" style={{ background: "linear-gradient(180deg, #0B0C0E 0%, #0E0F13 100%)"}}>
        <div className="section-container">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <div className="eyebrow mb-3">Curated Fleet</div>
              <h2 className="font-serif text-3xl sm:text-4xl text-white">Choose your class of comfort.</h2>
            </div>
            <Link to="/fleet" className="text-sm text-[color:var(--lume-gold)] flex items-center gap-2 hover:gap-3 transition-all">View the fleet <ArrowRight size={14}/></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FLEET.map((v) => (
              <div key={v.id} className="lume-card overflow-hidden group" data-testid={`fleet-preview-${v.id}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={v.image} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="font-serif text-xl text-white">{v.name}</div>
                    <span className="gold-badge">{v.capacity} Pax</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-2 italic">{v.tagline}</div>
                  {v.quote_only
                    ? <button onClick={() => navigate(`/contact?subject=quote&vehicle=${v.id}`)} className="mt-5 btn-outline-gold" data-testid={`fleet-preview-cta-${v.id}`}>Request a Quote</button>
                    : <button onClick={() => navigate(`/booking?vehicle=${v.id}`)} className="mt-5 btn-outline-gold" data-testid={`fleet-preview-cta-${v.id}`}>Request This Class</button>
                  }
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS strip */}
      {reviews.length > 0 && (
        <section className="py-24 section-container">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="eyebrow mb-3">Guest Impressions</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-white">Discretion. Precision. Presence.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {reviews.slice(0,4).map((r) => (
              <div key={r.id} className="lume-card p-6" data-testid={`home-review-${r.id}`}>
                <div className="text-[color:var(--lume-gold)] mb-3">{"★".repeat(r.rating)}</div>
                <p className="text-sm text-slate-300 leading-relaxed">"{r.comment}"</p>
                <div className="mt-4 text-xs uppercase tracking-widest text-slate-500">— {r.name} · {r.city}</div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/reviews" className="btn-outline-gold">Share Your Experience</Link>
          </div>
        </section>
      )}

      {/* CTA band */}
      <section className="section-container pb-24">
        <div className="lume-card p-10 sm:p-14 flex flex-col md:flex-row items-center justify-between gap-6" style={{ background: "linear-gradient(135deg, #131519 0%, #0B0C0E 100%)"}}>
          <div>
            <div className="eyebrow mb-3">Reserve With Confidence</div>
            <h3 className="font-serif text-2xl sm:text-3xl text-white">Your chauffeur is one reservation away.</h3>
          </div>
          <Link to="/booking" className="btn-gold" data-testid="cta-band-book">Book a Ride</Link>
        </div>
      </section>
    </div>
  );
}
